import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);

export const USER_TIMEZONE = dayjs.tz.guess();

const monthsAbbreviation = Array.from({ length: 12 }).map((_, index) =>
  new Date(0, index).toLocaleString('en', { month: 'short' }).toUpperCase()
);

export default class DateUtils {
  static formatDate(date: Date): string {
    let day = (date.getUTCDate() < 10 ? '0' : '') + date.getUTCDate();
    let month =
      (date.getUTCMonth() + 1 < 10 ? '0' : '') + (date.getUTCMonth() + 1);
    return `${month}/${day}/${date.getUTCFullYear()}`;
  }

  static getDifferenceDays(value1: string, value2: string) {
    // To set two dates to two variables
    const date1 = new Date(value1);
    const date2 = new Date(value2);
    // To calculate the time difference of two dates
    const Difference_In_Time = date2.getTime() - date1.getTime();
    // To calculate the no. of days between two dates
    const Difference_In_Days = Difference_In_Time / (1000 * 3600 * 24);
    return Math.ceil(Difference_In_Days);
  }

  static getDifferenceSeconds(value1: string, value2: string) {
    return dayjs(value2).diff(value1, 'second', true);
  }

  static getLocalDateISOString(dateISO: string): string {
    const dateTimezone = new Date(dateISO);
    dateTimezone.setTime(
      dateTimezone.getTime() - dateTimezone.getTimezoneOffset() * 60 * 1000
    );
    return dateTimezone.toISOString();
  }

  static hour24FormatTo12Format(hour: string) {
    if (Number(hour) > 12) {
      return String(Number(hour) % 12).padStart(2, '0');
    }
    return hour;
  }

  static formatISODate(dateISO: string, returnTime?: boolean): string {
    if (dateISO) {
      const dateLocal = DateUtils.getLocalDateISOString(dateISO);
      const [date, time] = dateLocal.split('T');
      const [year, month, day] = date.split('-');
      if (returnTime) {
        return `${month}/${day}/${year} ${DateUtils.timeConvert(
          time
            .split('.')[0]
            .split(':')
            .slice(0, 2)
            .join(':')
        )}`;
      }
      return `${month}/${day}/${year}`;
    }

    return '-';
  }

  static formatISOLongDate(
    dateISO: string,
    returnTime?: boolean,
    capitalized?: boolean
  ): string {
    if (dateISO) {
      const dateLocal = DateUtils.getLocalDateISOString(dateISO);
      const [date, time] = dateLocal.split('T');
      const [year, month, day] = date.split('-');
      if (returnTime) {
        return `${
          monthsAbbreviation[Number(month) - 1]
        } ${day}, ${year}, ${DateUtils.timeConvert(
          time
            .split('.')[0]
            .split(':')
            .slice(0, 2)
            .join(':')
        )}`;
      }

      let monthStr = monthsAbbreviation[Number(month) - 1];

      if (monthStr && capitalized) {
        monthStr = `${monthStr[0].toUpperCase()}${monthStr
          .slice(1)
          .toLowerCase()}`;
      }

      return `${monthStr} ${day}, ${year}`;
    }

    return '-';
  }

  static timeConvert(time: string) {
    const [hours, ...rest] = time.split(':');
    // If time format correct
    let res: string = '';
    res += +hours % 12 || 12; // Adjust hours
    res += `:${rest.join(':')} ${+hours < 12 ? 'AM' : 'PM'}`; // Set AM/PM

    return res; // return adjusted time or original string
  }

  static formatDateString(dateString: string): string {
    if (dateString == '') return dateString;

    // dateString as = 2019-09-21, should return 21/09/2019
    const [year, month, day] = dateString.split('-');

    return `${month}/${day}/${year}`;
  }

  static getFirstDayOfMonth(date: Date) {
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), 1);
  }

  static get minimumDatePickerDate(): Date {
    return DateUtils.getFirstDayOfMonth(new Date());
  }

  static get minimumCalendarDate(): Date {
    return new Date(2019, 0, 1);
  }

  static get maximumCalendarDate(): Date {
    const today = new Date();
    const oneYearLater = new Date(
      today.getUTCFullYear() + 1,
      today.getUTCMonth(),
      today.getUTCDate()
    );

    return oneYearLater;
  }

  static formatSeconds(seconds: number) {
    // create array of day, hour, minute and second values
    const unitConversions: [number, string, boolean?][] = [
      [Math.floor(seconds / (3600 * 24)), 'day'],
      [Math.floor((seconds % (3600 * 24)) / 3600), 'hour'],
      [Math.floor((seconds % 3600) / 60), 'minute'],
      [Math.floor(seconds % 60), 's', true]
    ];

    const plural = (value: number, unit: string): string => {
      if (value === 1) {
        return `${value} ${unit}`;
      }

      return `${value} ${unit}s`;
    };

    const res: string[] = [];

    unitConversions.forEach((arr: [number, string, boolean?], i: number) => {
      // if a truthy value
      const [value, sufix, noPlural] = arr;
      if (value) {
        let ret: string;

        // add the relevant value suffix
        if (noPlural) {
          ret = `${value}${sufix}`;
        } else {
          ret = plural(value, sufix);
        }

        res.push(ret);
      }
    });

    return res.slice(0, 2).join(' and ');
  }

  static get todayZeroHour() {
    let date = new Date();

    date.setUTCHours(0);
    date.setUTCMinutes(0);
    date.setUTCSeconds(0);
    date.setUTCMilliseconds(0);

    return date;
  }

  static getFromAndToDates(baseDate = new Date(), amount = 5) {
    const currentData = baseDate;

    const [from] = new Date(currentData.setDate(currentData.getDate() - amount))
      .toISOString()
      .split('T');

    const [to] = new Date().toISOString().split('T');

    return {
      from,
      to
    };
  }

  static formattedDate({
    date,
    format = 'MMM D, YYYY',
    applyTimezone = false,
    fallbackText = '--',
    fromNow = false
  }: {
    date: Date | string;
    format?: string;
    applyTimezone?: boolean;
    fallbackText?: string;
    fromNow?: boolean;
  }) {
    if (!dayjs(date).isValid()) return fallbackText;

    if (!applyTimezone) {
      if (fromNow) {
        return dayjs(date)
          .utc()
          .fromNow();
      }

      return dayjs(date)
        .utc()
        .format(format);
    }

    if (fromNow) {
      return dayjs(date).fromNow();
    }

    return dayjs(date).format(format);
  }

  getFormatedDate(date: string): string {
    let dateFormatted = new Date(date);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour12: true,
      hour: '2-digit',
      minute: '2-digit'
    };

    let newDateFormatted = dateFormatted.toLocaleString('en', options);
    return newDateFormatted;
  }

  static addDays(date: Date, days: number) {
    date.setDate(date.getDate() + days);
    return date;
  }

  getShortFormattedDate(date: string): string {
    let dateFormatted = new Date(date);
    let newDateFormatted = dateFormatted.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    return newDateFormatted;
  }
}
